import React, { useState } from 'react';
import { DatePicker } from '@/components/ui/DatePicker';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import { TimePicker } from '@/components/ui/TimePicker';
import { TimeRangePicker } from '@/components/ui/TimeRangePicker';
import { Stack } from '@/components/ui';
import { CodeBlock } from '../CodeBlock';
import { SectionWrapper } from '../SectionWrapper';

export function DateTimeSection() {
  // DatePicker states
  const [defaultDate, setDefaultDate] = useState<Date | null>(null);
  const [presetDate, setPresetDate] = useState<Date | null>(new Date('2026-04-10'));
  const [labeledDate, setLabeledDate] = useState<Date | null>(null);
  const [errorDate, setErrorDate] = useState<Date | null>(null);
  const [constrainedDate, setConstrainedDate] = useState<Date | null>(null);

  // DateRangePicker states
  const [defaultRange, setDefaultRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });
  const [presetRange, setPresetRange] = useState<{ start: Date | null; end: Date | null }>({
    start: new Date('2026-01-01'),
    end: new Date('2026-02-20'),
  });
  const [labeledRange, setLabeledRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });

  // TimePicker states
  const [defaultTime, setDefaultTime] = useState<string>('');
  const [time12h, setTime12h] = useState<string>('');
  const [labeledTime, setLabeledTime] = useState<string>('');
  const [errorTime, setErrorTime] = useState<string>('');
  const [constrainedTime, setConstrainedTime] = useState<string>('');
  const [steppedTime, setSteppedTime] = useState<string>('');

  // TimeRangePicker states
  const [defaultTimeRange, setDefaultTimeRange] = useState<{ start: string | null; end: string | null }>({ start: null, end: null });
  const [presetTimeRange, setPresetTimeRange] = useState<{ start: string | null; end: string | null }>({ start: '09:00', end: '17:00' });
  const [labeledTimeRange, setLabeledTimeRange] = useState<{ start: string | null; end: string | null }>({ start: null, end: null });
  const [overrideTimeRange, setOverrideTimeRange] = useState<{ start: string | null; end: string | null }>({ start: null, end: null });

  return (
    <SectionWrapper title="Date & Time Pickers">
      <div className="grid gap-8 md:grid-cols-2">
        {/* DatePicker */}
        <div>
          <h3 className="text-lg font-semibold mb-4">DatePicker</h3>
          <Stack spacing={4}>
            <DatePicker
              placeholder="Select a date"
              value={defaultDate}
              onChange={setDefaultDate}
            />
            <DatePicker
              label="Pre-set value"
              value={presetDate}
              onChange={setPresetDate}
            />
            <DatePicker
              label="Appointment date"
              hint="Choose a date for your appointment"
              value={labeledDate}
              onChange={setLabeledDate}
            />
            <DatePicker
              label="Event date"
              error="This date is already booked"
              value={errorDate}
              onChange={setErrorDate}
            />
            <DatePicker
              label="Archived date"
              value={new Date('2026-01-15')}
              disabled
            />
            <DatePicker
              label="Travel window"
              hint="Select a date between Apr 1 and Apr 30, 2026"
              minDate={new Date('2026-04-01')}
              maxDate={new Date('2026-04-30')}
              value={constrainedDate}
              onChange={setConstrainedDate}
            />
          </Stack>
          <CodeBlock>
{`import { DatePicker } from '@/components/ui/DatePicker';

<DatePicker
  placeholder="Select a date"
  value={value}
  onChange={setValue}
/>
<DatePicker
  label="Appointment date"
  hint="Choose a date for your appointment"
  value={value}
  onChange={setValue}
/>
<DatePicker
  label="Travel window"
  minDate={new Date('2026-04-01')}
  maxDate={new Date('2026-04-30')}
  value={value}
  onChange={setValue}
/>`}
          </CodeBlock>
        </div>

        {/* DateRangePicker */}
        <div>
          <h3 className="text-lg font-semibold mb-4">DateRangePicker</h3>
          <Stack spacing={4}>
            <DateRangePicker
              placeholder="Select date range"
              startDate={defaultRange.start}
              endDate={defaultRange.end}
              onChange={setDefaultRange}
            />
            <DateRangePicker
              label="Pre-set range"
              startDate={presetRange.start}
              endDate={presetRange.end}
              onChange={setPresetRange}
            />
            <DateRangePicker
              label="Project timeline"
              hint="Pick start and end dates for the project"
              startDate={labeledRange.start}
              endDate={labeledRange.end}
              onChange={setLabeledRange}
            />
            <DateRangePicker
              label="Archived range"
              startDate={new Date('2026-03-01')}
              endDate={new Date('2026-03-15')}
              disabled
            />
          </Stack>
          <CodeBlock>
{`import { DateRangePicker } from '@/components/ui/DateRangePicker';

<DateRangePicker
  placeholder="Select date range"
  startDate={range.start}
  endDate={range.end}
  onChange={setRange}
/>
<DateRangePicker
  label="Project timeline"
  hint="Pick start and end dates"
  startDate={range.start}
  endDate={range.end}
  onChange={setRange}
/>`}
          </CodeBlock>
        </div>

        {/* TimePicker */}
        <div>
          <h3 className="text-lg font-semibold mb-4">TimePicker</h3>
          <Stack spacing={4}>
            <TimePicker
              label="Default (uses app locale)"
              value={defaultTime}
              onChange={(v) => setDefaultTime(v ?? '')}
            />
            <TimePicker
              label="Time (12h override)"
              use24Hour={false}
              value={time12h}
              onChange={(v) => setTime12h(v ?? '')}
            />
            <TimePicker
              label="Meeting time"
              hint="Choose a convenient time"
              value={labeledTime}
              onChange={(v) => setLabeledTime(v ?? '')}
            />
            <TimePicker
              label="Deadline"
              error="Time must be during business hours"
              value={errorTime}
              onChange={(v) => setErrorTime(v ?? '')}
            />
            <TimePicker
              label="Locked time"
              value="09:00"
              disabled
            />
            <TimePicker
              label="Office hours only"
              hint="Between 09:00 and 17:00"
              minTime="09:00"
              maxTime="17:00"
              value={constrainedTime}
              onChange={(v) => setConstrainedTime(v ?? '')}
            />
            <TimePicker
              label="Quarter-hour slots"
              hint="15-minute intervals"
              minuteStep={15}
              value={steppedTime}
              onChange={(v) => setSteppedTime(v ?? '')}
            />
          </Stack>
          <CodeBlock>
{`import { TimePicker } from '@/components/ui/TimePicker';

<TimePicker
  label="Default (uses app locale)"
  value={value}
  onChange={setValue}
/>
<TimePicker
  label="Time (12h override)"
  use24Hour={false}
  value={value}
  onChange={setValue}
/>
<TimePicker
  label="Office hours only"
  minTime="09:00"
  maxTime="17:00"
  value={value}
  onChange={setValue}
/>
<TimePicker
  label="Quarter-hour slots"
  minuteStep={15}
  value={value}
  onChange={setValue}
/>`}
          </CodeBlock>
        </div>

        {/* TimeRangePicker */}
        <div>
          <h3 className="text-lg font-semibold mb-4">TimeRangePicker</h3>
          <Stack spacing={4}>
            <TimeRangePicker
              placeholder="Select time range"
              startTime={defaultTimeRange.start}
              endTime={defaultTimeRange.end}
              onChange={setDefaultTimeRange}
            />
            <TimeRangePicker
              label="Business hours"
              startTime={presetTimeRange.start}
              endTime={presetTimeRange.end}
              onChange={setPresetTimeRange}
            />
            <TimeRangePicker
              label="Availability window"
              hint="Set your available hours for the day"
              startTime={labeledTimeRange.start}
              endTime={labeledTimeRange.end}
              onChange={setLabeledTimeRange}
            />
            <TimeRangePicker
              label="Shift hours (12h override)"
              use24Hour={false}
              startTime={overrideTimeRange.start}
              endTime={overrideTimeRange.end}
              onChange={setOverrideTimeRange}
            />
          </Stack>
          <CodeBlock>
{`import { TimeRangePicker } from '@/components/ui/TimeRangePicker';

<TimeRangePicker
  placeholder="Select time range"
  startTime={range.start}
  endTime={range.end}
  onChange={setRange}
/>
<TimeRangePicker
  label="Availability window"
  hint="Set your available hours"
  startTime={range.start}
  endTime={range.end}
  onChange={setRange}
/>
<TimeRangePicker
  label="Shift hours (12h override)"
  use24Hour={false}
  startTime={range.start}
  endTime={range.end}
  onChange={setRange}
/>`}
          </CodeBlock>
        </div>
      </div>
    </SectionWrapper>
  );
}
